import {useState, useRef, useEffect} from 'react';
import styled from 'styled-components';
import {raisedSurfaceShadow} from '../../../styles/elevation';
import {MERGE_TAGS, type MergeTag} from '../utils/insertMergeTag';

interface Props {
	onSelect: (tag: MergeTag) => void;
	disabled?: boolean;
}

const Container = styled.div`
	position: relative;
	display: inline-block;
`;
const TriggerButton = styled.button`
	display: inline-flex;
	align-items: center;
	gap: 4px;
	border-radius: 4px;
	border: 1px solid #cbd5e1;
	background: #ffffff;
	padding: 6px 10px;
	font-size: 12px;
	line-height: 16px;
	font-weight: 500;
	color: #334155;
	cursor: pointer;
	&:hover {
		background: #f8fafc;
	}
	&:disabled {
		cursor: not-allowed;
		opacity: 0.5;
	}
`;
const TokenGlyph = styled.span`
	font-family: monospace;
	color: #d97706;
`;
const Popover = styled.div`
	position: absolute;
	left: 0;
	top: 100%;
	z-index: 10;
	margin-top: 4px;
	width: 14rem;
	border-radius: 8px;
	border: 1px solid #e2e8f0;
	background: #ffffff;
	padding: 4px 0;
	${raisedSurfaceShadow}
`;
const OptionButton = styled.button`
	display: flex;
	width: 100%;
	align-items: center;
	justify-content: space-between;
	padding: 8px 12px;
	text-align: left;
	font-size: 14px;
	line-height: 20px;
	border: 0;
	background: transparent;
	cursor: pointer;
	&:hover {
		background: #f8fafc;
	}
`;
const OptionLabel = styled.span`
	font-weight: 500;
	color: #334155;
`;
const OptionToken = styled.span`
	font-family: monospace;
	font-size: 12px;
	line-height: 16px;
	color: #94a3b8;
`;

export function MergeTagButton({onSelect, disabled}: Props) {
	const [open, setOpen] = useState(false);
	const containerRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!open) return;
		function handleClickOutside(e: MouseEvent) {
			if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
				setOpen(false);
			}
		}
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, [open]);

	return (
		<Container ref={containerRef}>
			<TriggerButton type="button" disabled={disabled} onClick={() => setOpen(!open)} title="Insert dynamic field">
				<TokenGlyph>{'{ }'}</TokenGlyph>
				<span>Fields</span>
			</TriggerButton>

			{open && (
				<Popover>
					{MERGE_TAGS.map(tag => (
						<OptionButton
							key={tag.token}
							type="button"
							onClick={() => {
								onSelect(tag);
								setOpen(false);
							}}
						>
							<OptionLabel>{tag.label}</OptionLabel>
							<OptionToken>[{tag.token}]</OptionToken>
						</OptionButton>
					))}
				</Popover>
			)}
		</Container>
	);
}
